import React from 'react';
import { ShieldCheck, Activity, AlertCircle, CheckCircle2, ChevronRight, Info } from 'lucide-react';
export function ScientificVariant() {
  return <div className="min-h-screen w-full bg-[#F8FAFC] text-[#0F172A] font-sans" data-id="element-205">
      {/* Clinical Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50" data-id="element-206">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center" data-id="element-207">
          <div className="flex items-center gap-2" data-id="element-208">
            <div className="w-8 h-8 bg-[#0EA5E9] rounded-md flex items-center justify-center text-white font-bold" data-id="element-209">
              C
            </div>
            <span className="font-bold text-lg tracking-tight text-slate-900" data-id="element-210">
              ChillHuaHua Research
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600" data-id="element-211">
            <span data-id="element-212">Anatomy</span>
            <span data-id="element-213">Biomechanics</span>
            <span data-id="element-214">Safety Data</span>
          </div>
          <button className="bg-[#0F172A] text-white px-4 py-2 rounded text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-215">
            View Product
          </button>
        </div>
      </header>

      <main className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-216">
        {/* Abstract / Intro */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 md:p-12 mb-12" data-id="element-217">
          <div className="inline-flex items-center gap-2 text-[#0EA5E9] font-bold text-xs uppercase tracking-wider mb-6" data-id="element-218">
            <Activity size={16} data-id="element-219" />
            <span data-id="element-220">Canine Health Report</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-221">
            The "Walk-Time Cough": Analysis of Leash Impact on Canine Tracheal
            Structures
          </h1>

          <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-222">
            <strong data-id="element-223">Summary:</strong> Most dog parents ignore the "walk-time
            cough" until it becomes severe. This article examines the mechanical
            stress placed on the canine neck during walks and proposes an
            anatomical solution.
          </p>

          <div className="flex flex-col md:flex-row gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100 text-sm text-slate-700" data-id="element-224">
            <div className="flex items-start gap-2" data-id="element-225">
              <AlertCircle size={16} className="text-amber-500 mt-0.5 shrink-0" data-id="element-226" />
              <span data-id="element-227">
                <strong data-id="element-228">Problem:</strong> Neck is not load-bearing
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-229">
              <AlertCircle size={16} className="text-amber-500 mt-0.5 shrink-0" data-id="element-230" />
              <span data-id="element-231">
                <strong data-id="element-232">Symptom:</strong> Coughing, gagging, "honking"
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-233">
              <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-234" />
              <span data-id="element-235">
                <strong data-id="element-236">Solution:</strong> Chest-distributed force
              </span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-8" data-id="element-237">
          {/* Main Content Column */}
          <div className="md:col-span-8 space-y-12" data-id="element-238">
            {/* Section 1: The Problem */}
            <section data-id="element-239">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3" data-id="element-240">
                <span className="bg-slate-100 text-slate-500 w-8 h-8 rounded-full flex items-center justify-center text-sm" data-id="element-241">
                  01
                </span>
                The Mechanical Failure of Collars
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600" data-id="element-242">
                <p data-id="element-243">
                  A dog’s neck is not designed to be a load-bearing handle. It
                  contains the trachea (windpipe), nerves, blood vessels, and
                  structures that aren’t meant to absorb sudden, repeated leash
                  force.
                </p>
                <p data-id="element-244">
                  When leash force hits the neck, it triggers two adverse
                  reactions:
                </p>
                <ul className="bg-white p-6 rounded-lg border border-slate-200 not-prose space-y-4 my-6" data-id="element-245">
                  <li className="flex gap-4" data-id="element-246">
                    <div className="w-1 h-full bg-red-500 rounded-full" data-id="element-247"></div>
                    <div data-id="element-248">
                      <strong className="block text-slate-900" data-id="element-249">
                        Physical Stress
                      </strong>
                      <span className="text-sm" data-id="element-250">
                        Direct compression of the trachea causing
                        coughing/gagging reflex.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4" data-id="element-251">
                    <div className="w-1 h-full bg-red-500 rounded-full" data-id="element-252"></div>
                    <div data-id="element-253">
                      <strong className="block text-slate-900" data-id="element-254">
                        Opposition Reflex
                      </strong>
                      <span className="text-sm" data-id="element-255">
                        Neurological response where dogs push into pressure,
                        increasing force.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 2: Anatomy Diagram Placeholder */}
            <section className="bg-[#0F172A] text-white rounded-xl p-8 overflow-hidden relative" data-id="element-256">
              <div className="relative z-10" data-id="element-257">
                <h3 className="text-xl font-bold mb-4" data-id="element-258">
                  Anatomical Comparison
                </h3>
                <div className="grid md:grid-cols-2 gap-8" data-id="element-259">
                  <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm" data-id="element-260">
                    <h4 className="font-bold text-red-400 mb-2" data-id="element-261">
                      Neck (Danger Zone)
                    </h4>
                    <ul className="text-sm space-y-2 text-slate-300" data-id="element-262">
                      <li data-id="element-263">• Trachea (Windpipe)</li>
                      <li data-id="element-264">• Thyroid Gland</li>
                      <li data-id="element-265">• Jugular Vein</li>
                      <li data-id="element-266">• Sensitive Nerves</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm border border-emerald-500/30" data-id="element-267">
                    <h4 className="font-bold text-emerald-400 mb-2" data-id="element-268">
                      Chest (Engine)
                    </h4>
                    <ul className="text-sm space-y-2 text-slate-300" data-id="element-269">
                      <li data-id="element-270">• Sternum (Bone)</li>
                      <li data-id="element-271">• Pectoral Muscles</li>
                      <li data-id="element-272">• Shoulder Blades</li>
                      <li data-id="element-273">• Load-bearing Structure</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-6 text-sm text-slate-400 italic" data-id="element-274">
                  Fig 1.1: Vulnerable neck structures vs. robust chest
                  musculature.
                </div>
              </div>
            </section>

            {/* Section 3: The Solution */}
            <section data-id="element-275">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3" data-id="element-276">
                <span className="bg-slate-100 text-slate-500 w-8 h-8 rounded-full flex items-center justify-center text-sm" data-id="element-277">
                  02
                </span>
                The "ChestFlow" Mechanism
              </h2>
              <p className="text-slate-600 mb-6" data-id="element-278">
                The ChillHuaHua harness implements a "ChestFlow" design that
                reroutes 100% of leash force away from the cervical spine and
                trachea, distributing it across the pectoral girdle.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-279">
                {['No tracheal compression during lunges', "Elimination of 'ride up' effect", 'Reduction in coughing triggers', 'Improved handler control'].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white p-4 rounded border border-slate-200 shadow-sm" data-id="element-280">
                    <ShieldCheck className="text-[#0EA5E9] shrink-0" size={20} data-id="element-281" />
                    <span className="text-sm font-medium text-slate-700" data-id="element-282">
                      {item}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Section 4: Data/Results */}
            <section data-id="element-283">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3" data-id="element-284">
                <span className="bg-slate-100 text-slate-500 w-8 h-8 rounded-full flex items-center justify-center text-sm" data-id="element-285">
                  03
                </span>
                Observed Outcomes
              </h2>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-286">
                <table className="w-full text-sm text-left" data-id="element-287">
                  <thead className="bg-slate-50 text-slate-500 font-medium uppercase tracking-wider" data-id="element-288">
                    <tr data-id="element-289">
                      <th className="px-6 py-4" data-id="element-290">Metric</th>
                      <th className="px-6 py-4" data-id="element-291">Standard Collar/Harness</th>
                      <th className="px-6 py-4 text-[#0EA5E9]" data-id="element-292">
                        ChillHuaHua Design
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-293">
                    <tr data-id="element-294">
                      <td className="px-6 py-4 font-medium text-slate-900" data-id="element-295">
                        Pressure Point
                      </td>
                      <td className="px-6 py-4 text-red-600" data-id="element-296">Trachea / Neck</td>
                      <td className="px-6 py-4 text-emerald-600" data-id="element-297">
                        Sternum / Chest
                      </td>
                    </tr>
                    <tr data-id="element-298">
                      <td className="px-6 py-4 font-medium text-slate-900" data-id="element-299">
                        Gag Reflex
                      </td>
                      <td className="px-6 py-4" data-id="element-300">Frequent</td>
                      <td className="px-6 py-4" data-id="element-301">Eliminated</td>
                    </tr>
                    <tr data-id="element-302">
                      <td className="px-6 py-4 font-medium text-slate-900" data-id="element-303">
                        Owner Anxiety
                      </td>
                      <td className="px-6 py-4" data-id="element-304">High (Guilt)</td>
                      <td className="px-6 py-4" data-id="element-305">Low (Confidence)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-4 space-y-6" data-id="element-306">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 sticky top-24" data-id="element-307">
              <h3 className="font-bold text-slate-900 mb-4" data-id="element-308">Key Findings</h3>
              <ul className="space-y-4 text-sm text-slate-600 mb-6" data-id="element-309">
                <li className="flex gap-2" data-id="element-310">
                  <Info size={16} className="text-[#0EA5E9] shrink-0 mt-0.5" data-id="element-311" />
                  <span data-id="element-312">
                    Coughing is a sign of tracheal trauma, not just excitement.
                  </span>
                </li>
                <li className="flex gap-2" data-id="element-313">
                  <Info size={16} className="text-[#0EA5E9] shrink-0 mt-0.5" data-id="element-314" />
                  <span data-id="element-315">
                    "No-pull" harnesses often fail by restricting movement or
                    riding up.
                  </span>
                </li>
                <li className="flex gap-2" data-id="element-316">
                  <Info size={16} className="text-[#0EA5E9] shrink-0 mt-0.5" data-id="element-317" />
                  <span data-id="element-318">
                    Mechanical design can solve behavioral issues (pulling).
                  </span>
                </li>
              </ul>

              <div className="bg-slate-50 p-4 rounded border border-slate-100 mb-6" data-id="element-319">
                <p className="text-xs font-bold text-slate-500 uppercase mb-2" data-id="element-320">
                  Recommendation
                </p>
                <p className="text-sm font-medium text-slate-900" data-id="element-321">
                  Switch to chest-led harness system immediately if coughing
                  presents.
                </p>
              </div>

              <button className="w-full bg-[#0EA5E9] text-white py-3 px-4 rounded font-bold hover:bg-[#0284C7] transition-colors flex items-center justify-center gap-2" data-id="element-322">
                Check Availability
                <ChevronRight size={16} data-id="element-323" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}